import type { MaybeRef } from 'vue'

import { useQuery } from '@tanstack/vue-query'
import { computed, unref } from 'vue'

import { useAxios } from '@/composables/use-axios'
import { useTeam } from '@/composables/use-team'

const { axiosInstance } = useAxios()

// ---------------------------------------------------------------------------
// Types (aligned with Go controller nc_cpa.go: NcCpaQueryReq / NcCpaQueryRes)
// ---------------------------------------------------------------------------

export type RollupDimKey
  = 'global'
    | 'channel_type'
    | 'platform'
    | 'account'
    | 'campaign'
    | 'set'
    | 'ad'

export interface NcCpaDistribution {
  p10: number
  p25: number
  p50: number
  p75: number
  p90: number
}

export interface NcCpaQueryItem {
  nc_cpa: number | null
  sum_attributed_spend: number
  sum_conversions_credit: number
  nc_first_order_count: number
  /** 平均客单价（用于后续 cohort 分群，前端暂未消费） */
  aov?: number
  /** 计入 AOV 的订单数（前端暂未消费） */
  aov_order_count?: number
  /** 基于 AOV 的 NC CPA 中位值（渠道 benchmark 的 p50 真值来源） */
  nccpa_aov?: number | null
  /** NC CPA 同行分位数分布（p10~p90，benchmark 色带与区间真值来源） */
  nccpa_distribute?: NcCpaDistribution | null
}

export interface NcCpaQueryRes {
  team_id: number
  window_end_date: string
  results: Record<string, NcCpaQueryItem>
}

export interface NcCpaQueryParams {
  rollup_dim_key: RollupDimKey
  dim_values?: string[]
  window?: string
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/**
 * Directly wraps `POST /v2-4/nc_cpa/query` as defined in:
 *   backend/data-console/internal/controller/nc_cpa.go:57
 *
 * 只返回后端真实数据：
 *   - 200 且带 results  -> 返回真实响应
 *   - 204（该 team 无 Rollup 数据）-> 返回 null（上层按 nc_cpa=null 展示「—」）
 *   - 4xx/5xx/网络错误  -> 抛出，交给 vue-query 处理（不再回退 mock）
 */
export function useNcCpaQuery(params: MaybeRef<NcCpaQueryParams>) {
  const { currentTeam } = useTeam()
  const teamId = computed(() => currentTeam.value?.id ?? Number(sessionStorage.getItem('team_id') || 0))

  return useQuery({
    queryKey: computed(() => [
      'nc_cpa',
      teamId.value,
      unref(params).rollup_dim_key,
      unref(params).dim_values,
      unref(params).window || '28d',
    ]),
    queryFn: async (): Promise<NcCpaQueryRes | null> => {
      const p = unref(params)
      const body: Record<string, any> = {
        team_id: teamId.value,
        rollup_dim_key: p.rollup_dim_key,
        window: p.window || '28d',
      }
      if (p.dim_values && p.dim_values.length)
        body.dim_values = p.dim_values

      const { data: envelope } = await axiosInstance.post<{ code: number, message: string, data: NcCpaQueryRes | null }>(
        `${import.meta.env.VITE_SERVER_API_DATA}/v2-4/nc_cpa/query`,
        body,
        { validateStatus: s => s === 200 || s === 204 },
      )
      // 后端统一信封 { code, message, data }；真实数据在 envelope.data。
      // 204 或 data 为空 -> 返回 null 让上层降级为「无数据」（展示「—」）。
      const payload = envelope?.data
      return (payload && payload.results) ? payload : null
    },
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  })
}
